import type { EditorView } from '@codemirror/view';

export function wrapSelection(view: EditorView, marker: string, markerEnd = marker): boolean {
  const sel = view.state.selection.main;
  const content = view.state.sliceDoc(sel.from, sel.to);
  const before = view.state.sliceDoc(Math.max(0, sel.from - marker.length), sel.from);
  const after = view.state.sliceDoc(sel.to, Math.min(view.state.doc.length, sel.to + markerEnd.length));
  if (before === marker && after === markerEnd) {
    view.dispatch({
      changes: [
        { from: sel.from - marker.length, to: sel.from, insert: '' },
        { from: sel.to, to: sel.to + markerEnd.length, insert: '' },
      ],
      selection: { anchor: sel.from - marker.length, head: sel.to - marker.length },
    });
    view.focus();
    return true;
  }
  view.dispatch({
    changes: { from: sel.from, to: sel.to, insert: marker + content + markerEnd },
    selection: { anchor: sel.from + marker.length, head: sel.from + marker.length + content.length },
  });
  view.focus();
  return true;
}

export function insertCode(view: EditorView): boolean {
  const sel = view.state.selection.main;
  const content = view.state.sliceDoc(sel.from, sel.to);
  const isMulti = content.includes('\n') || sel.from === sel.to;
  if (!isMulti) {
    return wrapSelection(view, '`');
  }
  const line = view.state.doc.lineAt(sel.from);
  const needsLeadingNewline = line.from !== sel.from || line.text.length > 0;
  const lead = needsLeadingNewline ? '\n' : '';
  const trail = '\n';
  const body = content || '';
  const insert = `${lead}\`\`\`\n${body}\n\`\`\`${trail}`;
  // Place cursor inside the block: right after the opening ```\n
  const inner = sel.from + lead.length + 4; // length of "```\n"
  view.dispatch({
    changes: { from: sel.from, to: sel.to, insert },
    selection: body
      ? { anchor: inner, head: inner + body.length }
      : { anchor: inner, head: inner },
  });
  view.focus();
  return true;
}

export function insertLink(view: EditorView): boolean {
  const sel = view.state.selection.main;
  const content = view.state.sliceDoc(sel.from, sel.to) || 'text';
  const insert = `[${content}](url)`;
  // place cursor on 'url'
  const urlFrom = sel.from + content.length + 3;
  const urlTo = urlFrom + 3;
  view.dispatch({
    changes: { from: sel.from, to: sel.to, insert },
    selection: { anchor: urlFrom, head: urlTo },
  });
  view.focus();
  return true;
}

const LINE_PREFIX_RE = /^(#{1,6}\s+|[-*]\s+|\d+\.\s+|>\s+)/;
const NUMBERED_RE = /^\d+\.\s+/;

export function prefixLine(view: EditorView, prefix: string): boolean {
  const sel = view.state.selection.main;
  const startLine = view.state.doc.lineAt(sel.from);
  const endLine = view.state.doc.lineAt(sel.to);
  const isNumbered = prefix === '1. ';
  const firstMatch = startLine.text.match(LINE_PREFIX_RE);
  const firstExisting = firstMatch ? firstMatch[0] : '';
  const shouldRemove = isNumbered
    ? NUMBERED_RE.test(firstExisting)
    : firstExisting === prefix;

  const changes: { from: number; to: number; insert: string }[] = [];
  let counter = 1;
  for (let n = startLine.number; n <= endLine.number; n++) {
    const line = view.state.doc.line(n);
    const match = line.text.match(LINE_PREFIX_RE);
    const existing = match ? match[0] : '';
    const stripLen = existing.length;
    let insert: string;
    if (shouldRemove) {
      const sameKind = isNumbered ? NUMBERED_RE.test(existing) : existing === prefix;
      insert = sameKind ? '' : existing;
    } else {
      insert = isNumbered ? `${counter++}. ` : prefix;
    }
    if (insert !== existing) {
      changes.push({ from: line.from, to: line.from + stripLen, insert });
    }
  }
  if (changes.length > 0) view.dispatch({ changes });
  view.focus();
  return true;
}
